import { gql } from "@apollo/client";
import { faCalculator } from "@fortawesome/free-solid-svg-icons";
import { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import DateInput from "components/common/form/inputs/date/DateInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import FormActions from "components/common/form/partials/FormActions";
import Page from "components/common/page/Page";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import WorkersInput, { WorkersFormType } from "components/entities/app/work/WorkersInput";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IdName } from "types/common-types";
import { DropdownInputPropsType } from "types/form-types";
import { AccommodationInvestmentFormPageQuery, AccommodationInvestmentFormPageQueryVariables, AccommodationInvestmentMutation, AccommodationInvestmentMutationVariables, AccommodationWorkInvestmentInput, InvestmentTypes, Units, WorkerInput } from "types/graphql";
import { SeasonType } from "types/season-type";
import { DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type WorkLogInvestmentFormType = {
    id: number | string,
    name: string,
    workers: WorkersFormType[],
    startDate: Date,
    note?: string,
    season: SeasonType,
    unit: Units,
    unitPrice: number | string,
    quantity: number | string,
    total: number | string,
    investmentType: InvestmentTypes
}

const query = gql`
query AccommodationInvestmentFormPage($id: Int!) {
    accommodationWorkInvestment(id: $id) {
      id,
      category,
      workers {
        items {
          person {
            id,
            name
          }
          id
          price
        }
      }
      startDate,
      season {
        id
        name,
        active
      }
      note
      name
      unit
      unitPrice
      quantity
      total
      investmentType
    }
    season(where: {active: {eq: true}}) {
      id,
      name,
      active
    }
  }
`;

const mutation = gql`
mutation AccommodationInvestment($model: AccommodationWorkInvestmentInput!) {
    accommodationWorkInvestment(model: $model) {
      id
    }
  }
`;

const AccommodationInvestmentFormPage = () => {
    const context = useAppContext();
    const params = useParams<{id: string}>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [model, setModel] = useState<Partial<WorkLogInvestmentFormType>>({
        workers: [],
        startDate: new Date(),
        id: 0,
        investmentType: undefined,
        name: '',
        note: '',
        quantity: '',
        season: undefined,
        total: '',
        unit: undefined,
        unitPrice: ''
    });

    const unitDropDownConfig: DropdownInputPropsType<Units> = {
        label: 'fields.unit.name',
        required: true,
        onChange: v => insertIntoModel('unit', v),
        dataSource: {
            data: [
                Units.Item,
                Units.Kg,
                Units.L
            ]
        },
        getItemId: i => i,
        view: {
            inputView: i => i
        }
    }

    const investmentTypeDropdownConfig: DropdownInputPropsType<InvestmentTypes> = {
        label: 'fields.investmentType.name',
        required: true,
        onChange: v => insertIntoModel('investmentType', v),
        dataSource: {
            data: [
                InvestmentTypes.Chemicals,
                InvestmentTypes.Equipments,
                InvestmentTypes.Other,
            ]
        },
        getItemId: i => i,
        view: {
            inputView: i => i
        }
    }

    useEffect(() => {
        load().then();
    }, [params.id]);

    const load = async () => {
        setComponentState('loading');
        try {
            const response = await graph<AccommodationInvestmentFormPageQuery, AccommodationInvestmentFormPageQueryVariables>({
                query,
                variables: {
                    id: Number(params.id) || 0
                }
            });

            if(response.errors) {
                throw response.errors;
            }

            
            let theModel: Partial<WorkLogInvestmentFormType> = {
                ...model,
                season: response.data!.season! as SeasonType
            }

            if(response.data?.accommodationWorkInvestment) {
                theModel = {
                    id: response.data.accommodationWorkInvestment.id,
                    name: response.data.accommodationWorkInvestment.name,
                    investmentType: response.data.accommodationWorkInvestment.investmentType,
                    note: response.data.accommodationWorkInvestment.note || '',
                    quantity: response.data.accommodationWorkInvestment.quantity,
                    startDate: new Date(response.data.accommodationWorkInvestment.startDate),
                    season: response.data.accommodationWorkInvestment.season,
                    total: response.data.accommodationWorkInvestment.total,
                    unit: response.data.accommodationWorkInvestment.unit,
                    unitPrice: response.data.accommodationWorkInvestment.unitPrice,
                    workers: response.data.accommodationWorkInvestment.workers!.items!.map(i => {
                        return {
                            id: i.id,
                            person: i.person as IdName,
                            price: i.price,
                        } as WorkersFormType;
                    })
                }
            }

            setModel(theModel);
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }

    
    const calculateTotal = (force?: boolean) => {
        const canChange = force || !model.total;
        if(Number(model.quantity) && Number(model.unitPrice) && canChange) {
            model.total = Number(model.quantity) * Number(model.unitPrice);
            insertIntoModel('total', model.total);
        }
    }
    
    const calculateUnitPrice = (force?: boolean) => {
        const canChange = force || !model.unitPrice;
        if(Number(model.quantity) && Number(model.total) && canChange) {
            model.unitPrice =  Number(model.total) / Number(model.quantity);
            insertIntoModel('unitPrice', model.unitPrice);
        }
    }

    const insertIntoModel = (prop: keyof WorkLogInvestmentFormType, value: any) => {
        model[prop] = value;
        setModel({
            ...model
        });
    }

    const onSubmit = async () => {
        setIsSubmitting(true);
        const modelDto: AccommodationWorkInvestmentInput = {
            name: model.name!,
            note: model.note,
            startDate: DateUtil.toIsoDate(model.startDate!),
            seasonId: model.season!.id,
            id: Number(model.id) || 0,
            unit: model.unit!,
            unitPrice: Number(model.unitPrice!),
            quantity: Number(model.quantity!),
            total: Number(model.total!),
            investmentType: model.investmentType!,
            workers: model.workers?.map(w => {
                return {
                    id: Number(w.id) || 0,
                    personId: Number(w.person!.id),
                    date: DateUtil.toIsoDate(model.startDate!),
                    price: Number(model.total!),
                    workId: Number(model.id) || 0,
                } as WorkerInput
            }) || []
        };
        try {
            const result = await graph<AccommodationInvestmentMutation, AccommodationInvestmentMutationVariables>({
                query: mutation,
                variables: {
                    model: modelDto
                }
            });

            if(result.errors) {
                throw result.errors;
            }

            context.notify('success', 'request.save.success');
            context.navigate(AppRoutes.ACCOMMODATION_INVESTMENT_VIEW, {id: result.data?.accommodationWorkInvestment?.id || 0}, {
                replace: true
            });
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Page title="pages.accommodation.work.investment.list" state={componentState}>
            <Form name="pages.accommodation.work.investment.list" onSubmit={onSubmit}>
            <fieldset>
                    <SeasonDropdown
                        value={model.season}
                        required
                        label="fields.season.name"
                        onChange={s => insertIntoModel('season', s)}
                    />
                    <Input
                        name="name"
                        value={model.name}
                        onChange={v => insertIntoModel('name', v)}
                        required
                        maxLength={128}
                        minLength={2}
                        label="fields.name.name"
                    />
                    <DateInput
                        required
                        label="fields.date.name"
                        value={model.startDate}
                        name="startDate"
                        onChange={d => insertIntoModel('startDate', d)}
                    />
                    <DropdownInput
                        {...investmentTypeDropdownConfig}
                        value={model.investmentType}
                    />
                </fieldset>
                <fieldset>
                    <Input
                        name="quantity"
                        required
                        label="fields.quantity.name"
                        type="number"
                        value={model.quantity?.toString() || ''}
                        onChange={v => insertIntoModel('quantity', v)}
                    />
                    <DropdownInput
                        {...unitDropDownConfig}
                        value={model.unit}
                    />
                    <Input
                        name="unitPrice"
                        required
                        label="fields.unitPrice.name"
                        type="number"
                        onFocus={() => calculateUnitPrice()}
                        value={model.unitPrice?.toString() || ''}
                        onChange={v => insertIntoModel('unitPrice', v)}
                        suffix={{icon: faCalculator, name: 'unitPrice', onClick: () => calculateUnitPrice(true)}}
                    />
                    <Input
                        name="total"
                        required
                        label="fields.total.name"
                        type="number"
                        value={model.total?.toString() || ''}
                        onFocus={() => calculateTotal()}
                        onChange={v => insertIntoModel('total', v)}
                        suffix={{icon: faCalculator, name: 'total', onClick: () => calculateTotal(true)}}
                    />
                </fieldset>
                <fieldset>
                    <WorkersInput
                        name="agricultureWorkInvestmentFormPage"
                        value={model.workers}
                        required
                        hidePrice
                        onChange={v => insertIntoModel('workers', v)}
                    />
                </fieldset>
                <fieldset>
                    <Textarea label="fields.note.name" name="note" value={model.note} onChange={v => setModel({ ...model, note: v })} />
                </fieldset>
                <FormActions disableInputs={isSubmitting} />
            </Form>
        </Page>
    );
};

export default AccommodationInvestmentFormPage;
